var WishlistController = (function () {
  const storageKey = 'glozin__wishlist-items';
  const customerWishlistedItems = window?.shopifyUser?.wishlistedItems || [];
  const syncDate = window?.shopifyUser?.wishlistSyncDate;
  const config = {
    /**
     * The wishlist behaviour when items added as a guest/logged-out user are synced upon login.
     *
     * Options:
     *
     *  - `merge` - merge all local items into the account's wishlisted items
     *  - `override` - replace local items with the logged-in account's wishlisted items. Note: only applicable when the account already has items.
     * @type {"merge" | "override"}
     */
    localSyncBehaviour: 'merge',
    userIdentityKey: 'big__user_identity',
  };

  return {
    async savedWishlistedItemsToAccount({ customerId, items = [] }) {
      const apiEndpoint = window?.API_ENDPOINT;
      if (!customerId) return console.error('Customer ID is required!');

      const response = await fetch(
        `${apiEndpoint}/customer/${customerId}/wishlist`,
        {
          method: 'POST',
          body: JSON.stringify({ productIds: items }),
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': window.API_KEY,
          },
        },
      );

      const body = await response.json();

      if (!body || !body?.success) {
        console.error('Error Saving wishlisted items! Reason: ', body?.error);

        throw 'Something went wrong while saving wishlists';
      }

      return body;
    },

    isWishlistedItem(productId) {
      return this.getItems().includes(productId);
    },

    /**
     * Updates wishlisted items according to the provided productId
     * @param {*} productId
     * @returns {string[]} - updated wishlisted items
     */
    setWishlistedItem(productId) {
      let updatedItems = [];

      const items = this.getItems();

      const isWishlistedItem = items.includes(productId);

      if (isWishlistedItem) {
        const filteredItems = items.filter((item) => item !== productId);

        updatedItems = filteredItems;
      } else {
        const newItems = [...items, productId];

        updatedItems = newItems;
      }

      return updatedItems;
    },
    /**
     * Retrieved the customer Id in the wishlistheader since the wishlist header is the one that syncs the wishlisted items on logged-in
     */
    getCustomerId() {
      return window?.shopifyUser?.id;
    },

    saveItemToStorage(items) {
      if (!Array.isArray(items))
        throw new Error('Wishlisted items must be an array!');

      // save to local storage
      localStorage.setItem(storageKey, JSON.stringify(items));
    },

    clearLocalWishlistStorage() {
      localStorage.setItem(storageKey, JSON.stringify([]));
    },

    getItems() {
      const localListProductIds = localStorage.getItem(storageKey);

      const parseLocalListProductIds = localListProductIds
        ? JSON.parse(localListProductIds)
        : [];

      return Array.isArray(parseLocalListProductIds)
        ? parseLocalListProductIds
        : [];
    },

    async mutate(productId) {
      const updatedItems = this.setWishlistedItem(productId);
      const isAddAction = this.isWishlistedItem(productId) ? false : true;
      const customerId = this.getCustomerId();

      if (customerId) {
        const response = await this.savedWishlistedItemsToAccount({
          customerId: customerId,
          items: updatedItems,
        });

        if (!response || !response?.success) {
          console.warn('Wishlist updated error: ', e);
          throw new Error('An error occurred while updating your wishlists!');
        }

        const data = response.data;
        const wishlistData = data.find((d) => d.key === 'wishlisted_items');

        const wishlistResults = wishlistData?.value;

        if (wishlistResults) {
          const parsedData = JSON.parse(wishlistResults);
          // set the filtered items in the backend removing inactive/missing products
          this.saveItemToStorage(parsedData);
        } else {
          this.saveItemToStorage(updatedItems);
        }
      } else {
        this.saveItemToStorage(updatedItems);
      }

      return {
        isAddAction,
        updatedItems,
      };
    },

    init() {
      const lastUserIdentity = this.getUserIdentity() ?? 'guest';

      // check if user is not logged-in and last identity is user
      if (!this.getCustomerId()) {
        if (lastUserIdentity === 'user') {
          this.clearLocalWishlistStorage();
          this.setUserIdentity('guest');
        }
      } else {
        this.setUserIdentity('user');
      }
      /**
       * Enforce sync when syncDate is not set and customerWishlistedItems is 0 or emptied and there,s an item present in local that does not added in user account and the last recorded identity is a guest
       */
      const shouldEnforeceSync =
        !syncDate ||
        (syncDate && customerWishlistedItems.length <= 0) ||
        (this.isOutOfSync() && lastUserIdentity === 'guest');

      if (shouldEnforeceSync) {
        this.sync();
      } else {
        // override existing items to user saved items
        this.saveItemToStorage(customerWishlistedItems);
      }
    },

    /**
     * Merge local and metafield wishlisted items
     * @returns {string[]} merged items
     */
    mergeWishlistItems() {
      const localItems = this.getItems();
      const mergedItems = [
        ...new Set([...localItems, ...customerWishlistedItems]),
      ];

      return mergedItems;
    },

    async sync() {
      try {
        const localItems = this.getItems();
        const customerId = this.getCustomerId();
        let toSyncItems = localItems;

        if (config.localSyncBehaviour === 'merge') {
          toSyncItems = this.mergeWishlistItems();
        }

        /**
         * Checks if items is out of sync, customerId is present and there's a wishlisted items present
         */
        if (this.isOutOfSync() && customerId && localItems.length > 0) {
          this.savedWishlistedItemsToAccount({
            items: toSyncItems,
            customerId: customerId,
          }).then((data) => {
            if (data?.success) {
              this.saveItemToStorage(toSyncItems);
              this.updateHeaderCount(toSyncItems);

              window.__hasPendingInitialWishlistReload = true;

              console.info('Wishlisted synced successfully!');
            }
          });
        }
      } catch (e) {
        console.warn('[Sync Wishlist Error]: ' + e);
      }
    },

    isOutOfSync() {
      const localItems = this.getItems();
      const set1 = new Set(localItems);
      const set2 = new Set(customerWishlistedItems);

      // Check if all items in set1 exist in set2
      return [...set1].some((item) => !set2.has(item));
    },

    updateHeaderCount(items = []) {
      const wishlistEl = document.querySelectorAll('.wishlist-count');
      wishlistEl.forEach((item) => {
        item.innerText = items !== null && items.length != 0 ? items.length : 0;
      });
    },

    /**
     * Set the user identity in which user is currently active
     * @param {"guest" | "user"} identity - user identity
     */
    setUserIdentity(identity) {
      localStorage.setItem(config.userIdentityKey, identity);
    },

    /**
     * Retrieved the user identity saved in the local storage
     * @returns
     */
    getUserIdentity() {
      return localStorage.getItem(config.userIdentityKey);
    },
  };
})();

WishlistController.init();
